import { Component, OnInit, Input } from '@angular/core';
import { appConfig } from 'src/app/_core/config/app-config.const';
import { CommonService } from 'src/app/_services/common.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';


@Component({
  selector: 'embedded-modal-popup',
  templateUrl: './embedded-modal-popup.component.html',
  styleUrls: ['./embedded-modal-popup.component.scss']
})
export class EmbeddedModalPopupComponent implements OnInit {
  iconPath = appConfig.iconPath;
  imgPath = appConfig.imagePath;
  
  // Input properties
  @Input() pathwayId: string = '';
  @Input() baseUrl: string = window.location.origin;
  
  // Embed modal properties
  selectedEmbedType: string = 'compact';
  
  // Copy state tracking
  copiedStates: { [key: string]: boolean } = {
    compact: false,
    standard: false,
    immersive: false
  };

  // Modified embed code for copying
  modifiedEmbedCode: string | null = null;

  constructor(
    private commonService: CommonService,
    private modalService: NgbModal,
    private router: Router,
    private sanitizer: DomSanitizer
    ) { }

  ngOnInit() { 
    // Validate inputs
    this.validateInputs();
  }
  
  /**
   * Validate required input properties
   */
  private validateInputs(): void {
    if (!this.pathwayId) {
      console.warn('EmbeddedModalPopupComponent: pathwayId is required but not provided');
    }
    if (!this.baseUrl) {
      console.warn('EmbeddedModalPopupComponent: baseUrl is required but not provided');
    }
  }
  
  /**
   * Check if component has valid inputs
   */
  hasValidInputs(): boolean {
    return !!(this.pathwayId && this.baseUrl);
  }
  
  /**
   * Get preview URL for specific type (without hash for display purposes)
   */
  getPreviewUrl(type: string): string {
    // if (!this.hasValidInputs()) {
    //   return '';
    // }
    
    let step = '';
    switch (type) {
      case 'compact':
        step = '?step=1';
        break;
      case 'standard':
        step = '?step=2';
        break;
      case 'immersive':
        step = '';
        break;
      default:
        step = '';
    }
    
    // Return URL without hash for display purposes
    return `${this.baseUrl}/#/dashboard/${this.pathwayId}/embedded${step}`;
  }

  /**
   * Get safe preview URL for iframe usage
   */
  getSafePreviewUrl(type: string): any {
    const previewUrl = this.getPreviewUrl(type);
    return this.sanitizer.bypassSecurityTrustResourceUrl(previewUrl);
  }

  /**
   * Get embed URL for specific type (with hash for actual embedding)
   */
  getEmbedUrl(type: string): string {
    // if (!this.hasValidInputs()) {
    //   return '';
    // }
    
    let step = '';
    switch (type) {
      case 'compact':
        step = '?step=1';
        break;
      case 'standard':
        step = '?step=2';
        break;
      case 'immersive':
        step = '';
        break;
      default:
        step = '';
    }
    
    return `${this.baseUrl}/#/dashboard/${this.pathwayId}/embedded${step}`;
  }
  
  /**
   * Get embed code (iframe HTML) for specific type
   */
  getEmbedCode(type: string): string {
    const embedUrl = this.getEmbedUrl(type);
    
    if (!embedUrl) {
      console.warn(`No embed URL available for type: ${type}`);
      return '';
    }
    
    const width = '100%';
    let height = '500';
    
    switch (type) {
      case 'compact':
        height = '500';
        break;
      case 'standard':
        height = '680';
        break;
      case 'immersive':
        height = '900';
        break;
      default:
        height = '500';
    }

    return `<iframe src="${embedUrl}" width="${width}" height="${height}" frameborder="0" allowfullscreen></iframe>`;
  }

  /**
   * Handle copy embed code (iframe HTML) from modal popup
   */
  handleCopyEmbedCode(type: string) {
    // Get the current embed code (either modified or original)
    const embedCode = this.getCurrentEmbedCode();
    
    // if (!embedCode) {
    //   console.error('No embed code available');
    //   return;
    // }
    
    this.copyToClipboard(embedCode, type, `${type} embed code copied to clipboard:`);
  }
  
  /**
   * Handle embed type selection from modal popup
   */
  handleEmbedTypeSelect(type: string) {
    this.selectedEmbedType = type;
    // Reset modified code when switching embed types
    this.modifiedEmbedCode = null;
    console.log('Selected embed type:', type);
  }
  
  /**
   * Handle embed code changes in the textarea
   */
  onCodeChange(event: any) {
    // Store the modified code for copying
    this.modifiedEmbedCode = event.target.value;
  }
  
  /**
   * Get the current embed code (either modified or original)
   */
  getCurrentEmbedCode(): string {
    return this.modifiedEmbedCode || this.getEmbedCode(this.selectedEmbedType);
  }
  
  /**
   * Copy text to clipboard with fallback support
   * @param text - text to copy
   * @param type - embed type for state management
   * @param successMessage - message to log on success
   */
  private copyToClipboard(text: string, type: string, successMessage: string) {
    if (!text) {
      console.error('No text to copy');
      return;
    }
    
    // Try to copy using the modern clipboard API
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => {
        // Set copied state to true
        this.copiedStates[type] = true;
        console.log(successMessage, text);
        
        // Reset the copied state after 2 seconds
        setTimeout(() => {
          this.copiedStates[type] = false;
        }, 2000);
      }).catch((error) => {
        console.error('Failed to copy to clipboard:', error);
        // Fallback to document.execCommand
        this.fallbackCopyToClipboard(text, type, successMessage);
      });
    } else {
      // Fallback for older browsers
      this.fallbackCopyToClipboard(text, type, successMessage);
    }
  }
  
  /**
   * Fallback method to copy to clipboard for older browsers
   * @param text - text to copy
   * @param type - embed type for state management
   * @param successMessage - message to log on success
   */
  private fallbackCopyToClipboard(text: string, type: string, successMessage: string) {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const result = document.execCommand('copy');
      if (result) {
        // Set copied state to true
        this.copiedStates[type] = true;
        console.log(successMessage + ' (fallback):', text);
        
        // Reset the copied state after 2 seconds
        setTimeout(() => {
          this.copiedStates[type] = false;
        }, 2000);
      } else {
        console.error('Failed to copy text');
      }
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
    
    document.body.removeChild(textArea);
  }
  
  /**
   * Close the embed modal
   */
  closeModal() {
    this.modalService.dismissAll();
  }
} 